import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, Mail, Globe, Linkedin, X, Instagram } from 'lucide-react';
import Logo from './Logo';


const Footer = () => {
  const footerSections = [
    {
      title: 'Company',
      links: [
        { name: 'About', path: '/about' },
        { name: 'Products', path: '/products' },
        { name: 'Projects', path: '/projects' },
        { name: 'Industries', path: '/industries' },
        { name: 'Contact', path: '/contact' }
      ]
    },
    {
      title: 'Solutions',
      links: [
        { name: 'AI & Machine Learning', path: '/solutions#ai-ml' },
        { name: 'Cloud Solutions', path: '/solutions#cloud' },
        { name: 'Custom Software', path: '/solutions#software' },
        { name: 'Marketing Intelligence', path: '/marketing' }
      ]
    }
  ];

  return (
    <footer className="bg-gradient-to-b from-black via-gray-950 to-black border-t border-cyan-400/30 relative overflow-hidden">
      {/* Ultra-Enhanced Animated Background */}
      <div className="absolute inset-0 opacity-15">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(rgba(0, 255, 255, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0, 255, 255, 0.1) 1px, transparent 1px),
              radial-gradient(circle at 25% 25%, rgba(34, 211, 238, 0.15) 0%, transparent 50%),
              radial-gradient(circle at 75% 75%, rgba(168, 85, 247, 0.15) 0%, transparent 50%),
              radial-gradient(circle, rgba(14, 165, 233, 0.08) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px, 60px 60px, 100% 100%, 100% 100%, 40px 40px',
            animation: 'gridPulse 6s ease-in-out infinite alternate'
          }}
        />
      </div>

      {/* Floating Digital Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400 rounded-full opacity-60"
            style={{
              left: `${10 + Math.random() * 80}%`,
              top: `${10 + Math.random() * 80}%`,
            }}
            animate={{
              y: [0, -20, 0],
              opacity: [0.6, 1, 0.6],
              scale: [1, 1.5, 1]
            }}
            transition={{
              duration: 4 + Math.random() * 3,
              repeat: Infinity,
              ease: "easeInOut",
              delay: Math.random() * 2
            }}
          />
        ))}
      </div>

      {/* Holographic Border Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent opacity-60 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-purple-500/50 to-transparent opacity-60 animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-20">
        {/* Simplified Header Section */}
        <div className="text-center mb-12 sm:mb-16">
          <motion.div
            className="inline-flex items-center gap-2 sm:gap-3 bg-gradient-to-r from-cyan-500/30 to-purple-600/30 px-4 sm:px-6 py-2 sm:py-3 rounded-full border-2 border-cyan-400/50 mb-6 sm:mb-8 backdrop-blur-lg"
            animate={{
              boxShadow: [
                '0 0 20px rgba(0, 255, 255, 0.4)',
                '0 0 40px rgba(168, 85, 247, 0.4)',
                '0 0 20px rgba(0, 255, 255, 0.4)'
              ]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <motion.span
              className="w-3 h-3 bg-cyan-400 rounded-full"
              animate={{
                scale: [1, 1.5, 1],
                opacity: [1, 0.5, 1]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <span className="text-cyan-400 text-xs sm:text-sm font-light tracking-wider uppercase">
              Connect With Us
            </span>
          </motion.div>

          <motion.h2
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extralight mb-4 sm:mb-6 leading-tight tracking-wide"
            animate={{
              textShadow: [
                '0 0 20px rgba(0, 255, 255, 0.6)',
                '0 0 40px rgba(168, 85, 247, 0.6)',
                '0 0 20px rgba(0, 255, 255, 0.6)'
              ]
            }}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <motion.span
              className="bg-gradient-to-r from-cyan-300 via-blue-400 to-purple-500 bg-clip-text text-transparent"
              animate={{
                backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                ease: "linear"
              }}
              style={{ backgroundSize: '200% 200%' }}
            >
              Intelligence Unleashed
            </motion.span>
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 sm:gap-12 mb-12 sm:mb-16 lg:mb-20">
          {/* Enhanced Company Info */}
          <div className="lg:col-span-2 text-center lg:text-left">
            <motion.div
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
              className="flex justify-center lg:justify-start"
            >
              <Logo className="mb-6 sm:mb-8 lg:mb-10" size="default" />
            </motion.div>
            <motion.p
              className="text-gray-300 mb-6 sm:mb-8 lg:mb-10 leading-relaxed text-sm sm:text-base max-w-md mx-auto lg:mx-0 font-light tracking-wide px-4 lg:px-0"
              style={{
                textShadow: '0 0 5px rgba(255, 255, 255, 0.1)'
              }}
            >
              Transform businesses with{" "}
              <span className="text-cyan-400">cutting-edge quantum AI solutions</span>{" "}
              that accelerate innovation, maximize efficiency, and unlock{" "}
              <span className="text-cyan-400">sustainable growth</span>.

            </motion.p>

            {/* Enhanced Social Media */}
            <div className="flex gap-4 sm:gap-6 justify-center lg:justify-start">
              {[
                { Icon: Linkedin, href: 'https://www.linkedin.com/in/qivra-tech-a1b54b3a9/', label: 'LinkedIn', color: 'from-blue-400 to-blue-600' },
                { Icon: X, href: 'https://x.com/Qivratech', label: 'X', color: 'from-gray-700 to-gray-900' },
                { Icon: Instagram, href: 'https://www.instagram.com/_qivratech_?igsh=MWw5bWpueWtyejd0bQ%3D%3D&utm_source=qr', label: 'Instagram', color: 'from-pink-400 to-purple-600' }
              ].map(({ Icon, href, label, color }) => (
                <motion.a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{
                    scale: 1.2,
                    y: -5,
                    boxShadow: '0 10px 30px rgba(0, 255, 255, 0.3)'
                  }}
                  whileTap={{ scale: 0.9 }}
                  className={`w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-xl sm:rounded-2xl flex items-center justify-center bg-gradient-to-br ${color} text-white hover:shadow-xl transition-all duration-300 border border-cyan-400/20 hover:border-cyan-400/60 backdrop-blur-sm relative overflow-hidden group touch-manipulation`}
                  aria-label={label}
                >
                  {/* Holographic overlay */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-cyan-400/20 to-purple-600/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    animate={{
                      background: [
                        'linear-gradient(45deg, rgba(0,255,255,0.2), rgba(168,85,247,0.2))',
                        'linear-gradient(225deg, rgba(168,85,247,0.2), rgba(0,255,255,0.2))',
                        'linear-gradient(45deg, rgba(0,255,255,0.2), rgba(168,85,247,0.2))'
                      ]
                    }}
                    transition={{ duration: 3, repeat: Infinity }}
                  />
                  <Icon size={window.innerWidth < 640 ? 18 : 24} className="relative z-10" />
                </motion.a>
              ))}
            </div>
          </div>

          {/* Links Sections */}
          {footerSections.map((section, idx) => (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: idx * 0.1 }}
              viewport={{ once: true }}
              className="text-center lg:text-left"
            >
              <h4 className="text-lg sm:text-xl font-extralight tracking-widest mb-6 sm:mb-8 text-white uppercase">{section.title}</h4>
              <ul className="space-y-3 sm:space-y-4">
                {section.links.map((link, linkIdx) => (
                  <motion.li
                    key={link.name}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: (idx * 0.1) + (linkIdx * 0.05) }}
                    viewport={{ once: true }}
                  >
                    <Link
                      to={link.path}
                      className="text-gray-300 hover:text-cyan-300 transition-all duration-300 flex items-center gap-3 group font-light tracking-wide lg:hover:translate-x-2 justify-center lg:justify-start touch-manipulation py-2"
                    >
                      <ChevronRight className="w-4 h-4 group-hover:translate-x-2 transition-transform duration-300 text-cyan-400/60 group-hover:text-cyan-400" />
                      <span className="text-sm sm:text-base">{link.name}</span>
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Simplified Bottom Bar */}
        <div className="pt-6 sm:pt-8 border-t border-gray-800/40">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 sm:gap-6">
            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-gray-400 font-light tracking-wide text-center md:text-left text-xs sm:text-sm"
            >
              © 2026 Qivra Technologies. Intelligence unleashed for the future.
            </motion.p>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="flex gap-4 sm:gap-6"
            >
              <Link to="/privacy" className="text-xs sm:text-sm text-gray-400 font-light hover:text-cyan-400 transition-colors duration-300 touch-manipulation py-2">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-xs sm:text-sm text-gray-400 font-light hover:text-cyan-400 transition-colors duration-300 touch-manipulation py-2">
                Terms of Service
              </Link>
            </motion.div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;